import React, {useRef} from 'react'
import styled from "@emotion/styled";
import sv from "../../styles/marketing";
import {useSize} from "../../hooks/commons/RefSize";
// Import Swiper React components & styles
import {Swiper, SwiperSlide} from "swiper/react";
import "swiper/swiper.min.css";
import SwiperCore, {Pagination} from 'swiper/core';
import {ExampleAllSlider} from "./showcase/ExampleAllSlider";
import {ExampleNoSlider} from "./showcase/ExampleNoSlider";
import {infoContainerWidth} from "./showcase/ExampleInfo";
import {exampleContainerWidth} from "./ExampleInstance";
import {ExampleTwoRowNoSlider} from "./showcase/ExampleTwoRowNoSlider";
import {ExampleTwoRowSlider} from "./showcase/ExampleTwoRowSlider";
// install Swiper modules
SwiperCore.use([Pagination]);

// STYLE ##########################################################

const Title = styled('h1')`
  ${sv.title};
  width: 100%;
  text-align: start;
  color: ${sv.colors.headline};
`;

const Block = styled('div')`
  margin: 30px 0;
`;

const BlockDetails = styled(`div`)`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin-top: 20px;
`;

const StyledSwiper = styled(Swiper)`
  max-width: 85vw;
  .swiper-pagination {
    bottom: 0;
  }
  .swiper-pagination-bullet-active {
    background: ${sv.colors.cta}
  }
  .swiper-slide {
    padding: 0 1vw;
  }
`;

// COMPONENT ##########################################################

/**
 * Returns slide info parameters
 *
 * @param width the available width
 * @param instance the example instance under consideration
 * @returns {{isSlide: boolean, isTwoTier: boolean, slidesPerView: number}} slide parameters
 */
const getSlideInfo = (width, instance) => {
  let isSlide = false, slidesPerView = 1;
  
  const exampleCount = instance.examples.length;    // number of example instances
  
  const expectedSizeSingleRow = infoContainerWidth + exampleContainerWidth; // minimum of (info and an example) for single-row view
  const isTwoTier = expectedSizeSingleRow > width;                          // if available width is less than the required width for single row view
  const expectedSizeExamplesOnly = exampleContainerWidth * exampleCount;    // total width needed for the examples only
  
  if(isTwoTier) {                                                           // Case : Two Tier
    if(expectedSizeExamplesOnly > width) {                                  // if available width is less then slider is needed
      isSlide = true;
      slidesPerView = width / exampleContainerWidth;
    }
  } else {                                                                  // Case : One Tier
    const expectedSizeInfoAndExamples = infoContainerWidth + expectedSizeExamplesOnly;  // width for info and examples combined
    if(expectedSizeInfoAndExamples > width) {                               // if available width is less then slider is needed
      isSlide = true;
      slidesPerView = width / exampleContainerWidth;
    }
  }

  return {
    "isSlide" : isSlide,
    "isTwoTier": isTwoTier,
    "slidesPerView": slidesPerView,
  }
}

// customized Swiper Carousel Props
const SwiperProps = {
  Swiper: StyledSwiper,
  SwiperSlide: SwiperSlide
}

export const ExampleCollection = ({ collectionInstance }) => {
  
  const examplesBlockRef = useRef(null);
  const size = useSize(examplesBlockRef)
  const isContainerSizeAvailable = size && size.width !== undefined;
  
  const slideInfo = isContainerSizeAvailable ? getSlideInfo(size.width, collectionInstance) : { isSlide: false };
  
  return (
      <Block ref={examplesBlockRef}>
        <Title>{collectionInstance.title}</Title>
        <BlockDetails>
          
          {/* if 2-tier is needed */}
          {slideInfo.isTwoTier &&
          <>
            
            {/* if slider is needed */}
            {slideInfo.isSlide &&
            <ExampleTwoRowSlider
                collectionInstance={collectionInstance}
                slidesPerView={slideInfo.slidesPerView}
                SwiperProps={SwiperProps}
            />
            }
    
            {/* if slider is not needed */}
            {!slideInfo.isSlide &&
            <ExampleTwoRowNoSlider
                collectionInstance={collectionInstance}
            />
            }
          </>
          }
  
          {/* if 1-tier is enough */}
          {!slideInfo.isTwoTier &&
          <>
            
            {/* if slider is needed */}
            {slideInfo.isSlide &&
            <ExampleAllSlider
                collectionInstance={collectionInstance}
                slidesPerView={slideInfo.slidesPerView}
                SwiperProps={SwiperProps}
            />
            }
  
            {/* if slider is not needed */}
            {!slideInfo.isSlide &&
            <ExampleNoSlider
                collectionInstance={collectionInstance}
            />
            }
          </>
          }
          
        </BlockDetails>
      </Block>
  );
}
